import React, { useEffect, useRef } from "react"; 
import { Control, Controller, UseFormSetValue } from "react-hook-form"; 
import { useTranslation } from "react-i18next";
 
import Whats from "../../../assets/images/icons/Ic_WhatsApp.svg";
import SMS from "../../../assets/images/icons/SMS_IC.svg";

import { useToast } from "../../../Services/ToastContext";
import { Loader } from "../../../components";
import { MessageBtn } from "../../../Share";

import useResendTimer from "../../../hooks/useResendTimer";
import { formatTime } from "../../../utils/timeHelpers";

interface OtpInputGroupProps {
  name: string;
  length?: number;
  control: Control<any>;
  setValue: UseFormSetValue<any>;

  isError?: boolean;
  errorMessage?: string;

  onComplete?: (otp: string[]) => void;

  resendData?: any;

  onResendSMS: () => void;
  onResendWhatsApp: () => void;

  maxResendAttempts?: number;

  isPending?: boolean;

  showResendOptions: boolean;

  setShowResendOptions: (show: boolean) => void;

  resetFields?: () => void;
}

const OtpInputGroup: React.FC<OtpInputGroupProps> = ({
  name,
  length = 6,
  control,
  setValue,

  isError = false,
  errorMessage,

  onComplete,

  resendData,

  onResendSMS,
  onResendWhatsApp,

  maxResendAttempts = 4,

  isPending = false,

  showResendOptions,
  setShowResendOptions,

  resetFields,
}) => {
  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);

  const { t } = useTranslation();

  const { showToast } = useToast();

  const { resendCodeCount, timeLeft } = useResendTimer(resendData);

  useEffect(() => {
    const timer = setTimeout(() => {
      inputsRef.current[0]?.focus();
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  const handleChange = (value: string, index: number) => {
    if (!/^\d?$/.test(value)) {
      return;
    }

    setValue(`${name}.${index}`, value);

    if (value.length === 1 && index < length - 1) {
      inputsRef.current[index + 1]?.focus();
    } else if (value === "" && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }

    setTimeout(() => {
      const currentValues = inputsRef.current.map((ref) => ref?.value ?? "");

      if (
        onComplete &&
        currentValues.length === length &&
        currentValues.every((char) => char !== "")
      ) {
        onComplete(currentValues);
      }
    }, 0);
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();

    const pasteData = e.clipboardData.getData("Text").trim();

    const pattern = new RegExp(`^\\d{${length}}$`);

    if (!pattern.test(pasteData)) {
      showToast(t("invalid_code_try_again"), "error");

      return;
    }

    for (let i = 0; i < length; i++) {
      setValue(`${name}.${i}`, pasteData.charAt(i));
    }

    inputsRef.current[length - 1]?.focus();

    onComplete?.(pasteData.split(""));
  };

  const handleResendCode = () => {
    if (resendCodeCount >= maxResendAttempts) {
      showToast(t("user_not_able_to_send_more_codes_until_24_hours"), "error");

      return;
    }

    if (timeLeft > 0) {
      showToast(t("please_wait_before_resending"), "error");

      return;
    }

    setShowResendOptions(true);

    resetFields?.();
  };

  return (
    <>
      <div className="mb-8 flex justify-between" dir="ltr">
        {Array.from({
          length,
        }).map((_, index) => (
          <Controller
            key={index}
            name={`${name}.${index}`}
            control={control}
            render={({ field }) => (
              <input
                className={`h-[50px] w-[75px] rounded-md border border-systemBg text-center text-2xl focus:outline-primary ${
                  isError
                    ? "border-2 border-solid border-error"
                    : field.value
                      ? "border-primary"
                      : "focus:outline-primary"
                } ${index === 2 ? "me-8" : "me-2"}`}
                maxLength={1}
                {...field}
                value={field.value || ""}
                onChange={(e) => handleChange(e.target.value, index)}
                ref={(el) => {
                  field.ref(el);
                  inputsRef.current[index] = el;
                }}
                aria-label={t("verification_code_digit", {
                  digit: index + 1,
                })}
                onPaste={index === 0 ? handlePaste : undefined}
                disabled={isPending}
              />
            )}
          />
        ))}
      </div>

      {isError && errorMessage && (
        <p className="mb-4 text-center text-error">{errorMessage}</p>
      )}

      <div className="mb-4 flex items-center justify-center gap-4">
        {!showResendOptions && (
          <button
            type="button"
            className={`${
              timeLeft > 0 || resendCodeCount >= maxResendAttempts
                ? "cursor-not-allowed text-body-text underline underline-offset-4"
                : "cursor-pointer font-bold text-primary underline underline-offset-4"
            }`}
            onClick={handleResendCode}
            disabled={
              timeLeft > 0 || resendCodeCount >= maxResendAttempts || isPending
            }
          >
            {t("send_code_again")}
          </button>
        )}

        {timeLeft > 0 && (
          <p dir="ltr" className="mt-1 text-body-text">
            {formatTime(timeLeft)}
          </p>
        )}
      </div>

      {showResendOptions && (
        <div className="my-4 flex flex-col items-center gap-4">
          <MessageBtn
            icon={Whats}
            title={t("send_code_via_whatsapp")}
            onClick={onResendWhatsApp}
            disabled={isPending}
          />

          <MessageBtn
            icon={SMS}
            title={t("send_code_via_sms")}
            onClick={onResendSMS}
            disabled={isPending}
            color="blue"
          />
        </div>
      )}

      {resendCodeCount >= maxResendAttempts && (
        <p className="mb-4 text-center text-error">
          {t("user_not_able_to_send_more_codes_until_24_hours")}
        </p>
      )}

      {isPending && <Loader />}
    </>
  );
};

export default OtpInputGroup;
